/**
 * Step 2 of reading a statement: the extractor profile for the detected bank (see bankRegistry.js).
 *
 * All banks share one engine (statementPdf.js: lines → header → rows → amounts → balance check).
 * A profile only describes what is special about a bank's layout:
 *   bandMode     "below"  – wrapped narration follows the date line (default)
 *                "around" – rows are vertically centred: narration sits above AND below the date line
 *   narrationWrap "cut"  – the bank cuts long narration mid-word when the column is full → join lines directly
 *                default – lines are joined with a space (word-wrapped or unknown layouts)
 *   aliases      { columnKey: [RegExp, …] }  extra header words for a column
 *   newestFirst  true | false                 force row order when it can't be detected
 *   columns      [{ key, x, right }]          a manual column mapping (saved from the in-app "map the columns" screen)
 *   extract      (pages, ctx) => result       full custom extractor, only for a bank the engine truly can't handle
 *   notes        what the layout looks like (for whoever maintains this)
 *
 * Adding a bank: put a sample PDF in client/.statement-fixtures/, run `npm run test:statements`,
 * add/adjust its profile here until it's ✓, and check every other bank is still ✓.
 */
export const BANK_PROFILES = {
  IBKL: {
    name: "IDBI Bank",
    bandMode: "below",
    narrationWrap: "cut",
    notes: "3-line table header (Withdrawals/(Dr)…), S.No column, date with time, balance printed on its own line "
      + "between narration lines, newest first, 'Dr Count / Cr Count / Debits / Credits' summary at the end."
  },
  HDFC: {
    name: "HDFC Bank",
    bandMode: "below",
    narrationWrap: "cut",
    notes: "Account block and table header repeated on every page, dd/mm/yy dates, narration wraps onto 1–3 lines "
      + "below the date line (~17pt apart), separate Chq./Ref.No. and Value Dt columns, statement summary at the end. "
      + "PDF is password protected."
  },
  TMBL: {
    name: "Tamilnad Mercantile Bank",
    bandMode: "around",
    narrationWrap: "cut",
    notes: "Header 'Date / Value Date / CHQ / Remarks / Debit / Credit / Balance'; rows vertically centred so the "
      + "Remarks text sits above and below the date line; overdraft accounts show negative balances; a 'Total' row "
      + "under Debit/Credit; legend at the end mentions 'MBank Credit Card Payment' (not a card statement)."
  }
};

/** Profile for a bank code (IFSC prefix), merged with a user-saved mapping for that bank if any. */
export function profileForBank(code, saved = null) {
  const builtIn = BANK_PROFILES[String(code || "").toUpperCase()] || null;
  if (!builtIn && !saved) return null;
  return {
    ...(builtIn || {}),
    ...(saved || {}),
    aliases: { ...(builtIn?.aliases || {}), ...(saved?.aliases || {}) }
  };
}

/** Back-compat helper: profile from an IFSC code. */
export function profileForIfsc(ifsc, saved = null) {
  return profileForBank(String(ifsc || "").slice(0, 4), saved);
}
